/**
 * O funil do CRM — trilha T7: vários pipelines por clínica, com etapas que se
 * renomeiam, reordenam e arquivam.
 *
 * As regras que a tela e os dois adaptadores compartilham moram aqui, sem React e
 * sem banco, para serem testadas sozinhas. O banco (`0100`) repete as que
 * importam — quem recusa de verdade é ele; aqui é para a tela dizer antes.
 */

import type { CargaDoPipeline, CorDaEtapa, EtapaDoFunil, Funil, Lead, SituacaoDoLead, TipoDaEtapa } from './tipos';

/** A cor da etapa, pela paleta de tokens. Semântica onde a etapa é situação (ganho). */
export const CORES_DA_ETAPA: Record<CorDaEtapa, string> = {
    neutro: 'var(--neutral-400)',
    info: 'var(--info)',
    aviso: 'var(--warning)',
    marca: 'var(--burgundy-500)',
    sucesso: 'var(--success)',
    perigo: 'var(--danger)',
};

export const OPCOES_DE_COR: { value: CorDaEtapa; label: string }[] = [
    { value: 'neutro', label: 'Neutra' },
    { value: 'info', label: 'Azul' },
    { value: 'aviso', label: 'Âmbar' },
    { value: 'marca', label: 'Bordô' },
    { value: 'sucesso', label: 'Verde' },
    { value: 'perigo', label: 'Vermelha' },
];

export const TOM_DA_SITUACAO: Record<SituacaoDoLead, 'warning' | 'success' | 'neutral'> = {
    aberta: 'warning',
    ganho: 'success',
    perdido: 'neutral',
};

const PESO: Record<TipoDaEtapa, number> = { aberta: 0, ganho: 1, perdido: 2 };

/** Abertas na ordem da clínica; depois a de ganho; por fim a de perda. */
export function ordenarEtapas(etapas: EtapaDoFunil[]): EtapaDoFunil[] {
    return [...etapas].sort((a, b) => PESO[a.tipo] - PESO[b.tipo] || a.ordem - b.ordem);
}

export function etapasAtivas(funil: Funil): EtapaDoFunil[] {
    return ordenarEtapas(funil.etapas.filter((e) => !e.arquivada));
}

/**
 * As colunas do quadro: as abertas e a de ganho.
 *
 * Perdido não vira coluna: o funil é o caminho até a venda, e uma coluna de
 * perdidos crescendo para sempre esconderia o que importa. O lead perdido
 * continua na lista e nos relatórios.
 */
export function colunasDoQuadro(funil: Funil): EtapaDoFunil[] {
    return etapasAtivas(funil).filter((e) => e.tipo !== 'perdido');
}

/** A etapa seguinte no quadro. Ganho e perdido não têm próxima. */
export function proximaEtapa(funil: Funil, etapaId: string): EtapaDoFunil | undefined {
    const colunas = colunasDoQuadro(funil);
    const indice = colunas.findIndex((e) => e.id === etapaId);
    const atual = colunas[indice];
    if (!atual || atual.tipo !== 'aberta') return undefined;
    return colunas[indice + 1];
}

export function etapaFinal(funil: Funil, tipo: 'ganho' | 'perdido'): EtapaDoFunil | undefined {
    return funil.etapas.find((e) => e.tipo === tipo);
}

/** O funil que recebe o lead novo quando ninguém escolhe: o primeiro da clínica. */
export function funilPadrao(funis: Funil[]): Funil | undefined {
    return [...funis].sort((a, b) => a.ordem - b.ordem)[0];
}

/** Os pipelines em ordem, cada um com as etapas em ordem. */
export function ordenarFunis(funis: Funil[]): Funil[] {
    return [...funis].sort((a, b) => a.ordem - b.ordem).map((f) => ({ ...f, etapas: ordenarEtapas(f.etapas) }));
}

/**
 * A carga do quadro, a partir dos leads DO pipeline mostrado. A mesma nos dois
 * adaptadores: o que é coluna, o que conta no total e o que é "em jogo".
 */
export function montarPipeline(funis: Funil[], funil: Funil, leadsDoFunil: Lead[], responsaveis: string[]): CargaDoPipeline {
    return {
        funis: ordenarFunis(funis),
        funil: { ...funil, etapas: ordenarEtapas(funil.etapas) },
        colunas: colunasDoQuadro(funil).map((e) => {
            const daEtapa = leadsDoFunil.filter((l) => l.etapaId === e.id);
            return {
                id: e.id, titulo: e.titulo, tipo: e.tipo, cor: e.cor,
                totalCentavos: daEtapa.reduce((s, l) => s + l.valorEstimadoCentavos, 0),
                leads: daEtapa,
            };
        }),
        leads: leadsDoFunil,
        /* "Em jogo" é o que ainda pode virar venda ou já virou — o perdido não conta. */
        totalCentavos: leadsDoFunil.filter((l) => l.situacao !== 'perdido').reduce((s, l) => s + l.valorEstimadoCentavos, 0),
        responsaveis,
    };
}

export type ModeloDeFunil = 'comercial' | 'posvenda' | 'vazio';

interface EtapaDoModelo {
    titulo: string;
    tipo: TipoDaEtapa;
    cor: CorDaEtapa;
}

/**
 * Os modelos do modal "Novo pipeline" do protótipo. Todo modelo ganha a etapa de
 * perda (o protótipo não a mostrava, e sem ela não há onde o lead perdido ficar);
 * "Começar em branco" ganha uma aberta, porque funil sem etapa aberta não tem onde
 * um lead entrar. Os mesmos três moram em `criar_funil` (0100).
 */
export const MODELOS_DE_FUNIL: { value: ModeloDeFunil; label: string; etapas: EtapaDoModelo[] }[] = [
    {
        value: 'comercial', label: 'Comercial — Entrada, Em contato, Proposta, Ganho',
        etapas: [
            { titulo: 'Entrada', tipo: 'aberta', cor: 'neutro' },
            { titulo: 'Em contato', tipo: 'aberta', cor: 'info' },
            { titulo: 'Proposta', tipo: 'aberta', cor: 'marca' },
            { titulo: 'Ganho', tipo: 'ganho', cor: 'sucesso' },
            { titulo: 'Perdido', tipo: 'perdido', cor: 'neutro' },
        ],
    },
    {
        value: 'posvenda', label: 'Pós-venda — Retorno, Contatado, Reagendado, Concluído',
        etapas: [
            { titulo: 'Retorno', tipo: 'aberta', cor: 'neutro' },
            { titulo: 'Contatado', tipo: 'aberta', cor: 'info' },
            { titulo: 'Reagendado', tipo: 'aberta', cor: 'marca' },
            { titulo: 'Concluído', tipo: 'ganho', cor: 'sucesso' },
            { titulo: 'Perdido', tipo: 'perdido', cor: 'neutro' },
        ],
    },
    {
        value: 'vazio', label: 'Começar em branco',
        etapas: [
            { titulo: 'Entrada', tipo: 'aberta', cor: 'neutro' },
            { titulo: 'Ganho', tipo: 'ganho', cor: 'sucesso' },
            { titulo: 'Perdido', tipo: 'perdido', cor: 'neutro' },
        ],
    },
];

export function etapasDoModelo(modelo: ModeloDeFunil): EtapaDoModelo[] {
    return (MODELOS_DE_FUNIL.find((m) => m.value === modelo)?.etapas ?? []).map((e) => ({ ...e }));
}

/** Uma linha do editor de etapas. Sem `id`, é etapa aberta nova. */
export interface EtapaEditada {
    id?: string;
    titulo: string;
    cor: CorDaEtapa;
    tipo: TipoDaEtapa;
}

/** O que o banco recusaria ao salvar, dito antes. `null` quando está tudo certo. */
export function problemaDaEdicao(etapas: EtapaEditada[]): string | null {
    const vistos = new Set<string>();
    for (const e of etapas) {
        const titulo = e.titulo.trim();
        if (!titulo) return 'Toda etapa precisa de título.';
        if (titulo.length > 40) return `O título "${titulo.slice(0, 20)}…" passa de 40 caracteres.`;
        const chave = titulo.toLocaleLowerCase('pt-BR');
        if (vistos.has(chave)) return `Duas etapas com o título "${titulo}".`;
        vistos.add(chave);
    }
    if (!etapas.some((e) => e.tipo === 'aberta')) return 'O pipeline precisa de ao menos uma etapa aberta.';
    if (etapas.filter((e) => e.tipo === 'ganho').length !== 1 || etapas.filter((e) => e.tipo === 'perdido').length !== 1) {
        return 'O pipeline precisa de uma etapa de ganho e uma de perda.';
    }
    return null;
}

/**
 * Sobe ou desce uma etapa ABERTA entre as abertas. As finais não se movem: ficam
 * sempre depois, porque "avançar" depois do ganho não existe.
 */
export function moverEtapa(etapas: EtapaEditada[], indice: number, direcao: -1 | 1): EtapaEditada[] {
    const alvo = indice + direcao;
    const atual = etapas[indice];
    const vizinha = etapas[alvo];
    if (!atual || !vizinha || atual.tipo !== 'aberta' || vizinha.tipo !== 'aberta') return etapas;
    const copia = [...etapas];
    copia[indice] = vizinha;
    copia[alvo] = atual;
    return copia;
}
